/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { LogLevel } from "@azure/msal-browser";
import { apiRoutes } from "./commons/constants";
import {Api} from "@mui/icons-material";

/**
 * Enter here the user flows and custom policies for your B2C application
 * To learn more about user flows, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/user-flow-overview
 * To learn more about custom policies, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/custom-policy-overview
 */
const clientId = process.env.REACT_APP_CLIENTID;

export const b2cPolicies = {
    names: {
        signUpSignIn: 'B2C_1_DashboardSignIn',
        forgotPassword: 'B2C_1_reset_v3',
        editProfile: 'B2C_1_edit_profile_v2',
    },
    authorities: {
        signUpSignIn: {
            authority: 'https://dashicmobile.b2clogin.com/dashicmobile.onmicrosoft.com/B2C_1_DashboardSignIn',
        },
        forgotPassword: {
            authority: 'https://dashicmobile.b2clogin.com/dashicmobile.onmicrosoft.com/B2C_1_reset_v3',
        },
        editProfile: {
            authority: 'https://dashicmobile.b2clogin.com/dashicmobile.onmicrosoft.com/b2c_1_edit_profile_v2',
        },
    },
    authorityDomain: 'dashicmobile.b2clogin.com',
};


/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
export const msalConfig = {
    auth: {
       // clientId: 'f84b67dc-49ea-45b6-8211-07f21dfe5a0a', // This is the ONLY mandatory field that you need to supply.
        clientId: clientId, // This is the ONLY mandatory field that you need to supply.
        clientSecret: '',
        authority: b2cPolicies.authorities.signUpSignIn.authority, // Choose SUSI as your default authority.
        knownAuthorities: [b2cPolicies.authorityDomain], // Mark your B2C tenant's domain as trusted.
        redirectUri: '/', // You must register this URI on Azure Portal/App Registration. Defaults to window.location.origin
        postLogoutRedirectUri: '/', // Indicates the page to navigate after logout.
        navigateToLoginRequestUrl: true, // If "true", will navigate back to the original request location before processing the auth code response.
    },
    cache: {
        cacheLocation: 'localStorage', // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
        storeAuthStateInCookie: true, // Set this to "true" if you are having issues on IE11 or Edge
    },
    system: {
        loggerOptions: {
            loggerCallback: (level, message, containsPii) => {
                if (containsPii) {
                    return;
                }
                switch (level) {
                    case LogLevel.Error:
                        console.error(message);
                        
                        return;
                    case LogLevel.Info:
                        console.info(message);
                        
                        return;
                    case LogLevel.Verbose:
                        console.debug(message);
                        
                        return;
                    case LogLevel.Warning:
                        console.warn(message);
                        
                        return;
                    default:
                        
                        return;
                }
            },
        },
    },
};

/**
 * Add here the endpoints and scopes when obtaining an access token for protected web APIs. For more information, see:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 */
export const protectedResources = {
  apiAddUser: {
    endpoint: apiRoutes.ADD_USER,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.write'],
    },
  },
  apiUserDetails: {
    endpoint: apiRoutes.USER_DETAIL,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.write'],
    },
  },
  apiUserKPI: {
    endpoint: apiRoutes.USER_KPI,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.write'],
    },
  },
  apiUserType: {
    endpoint: apiRoutes.USER_TYPE,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.write'],
    },
  },
  apiUserTerms: {
    endpoint: apiRoutes.USER_TERMS,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.write'],
    },
  },
  agencyReferral: {
    endpoint: apiRoutes.AGENCY_REFERRAL_LIST,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.write'],
    },
  },
  KPICategoryType: {
    endpoint: apiRoutes.CATEGORY_TYPE,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.write'],
    },
  },
  apiAdvertiserList: {
    endpoint: apiRoutes.ADVERTISER_LIST,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.write'],
    },
  },
  apiAgencyList: {
    endpoint: apiRoutes.AGENCY_LIST,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.write'],
    },
  },
  apiAgency: {
    endpoint: apiRoutes.AGENCY,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.write'],
    },
  },
  apiInsertionOrderList: {
    endpoint: apiRoutes.INSERTION_ORDER_LIST,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.write'],
    },
  },
  apiInsertionOrderByType: {
    endpoint: apiRoutes.INSERTION_ORDER_BYTYPE,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.write'],
    },
  },
  apiPipedriveInsertionOrderList: {
    endpoint: apiRoutes.PIPEDRIVE_INSERTION_ORDER_LIST,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.write'],
    },
  },
  apiPipedriveUpdateOrderList: {
    endpoint: apiRoutes.PIPEDRIVE_UPDATE_ORDER_LIST,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.write'],
    },
  },
  apiPipedriveUpdateAndConfirmOrderList: {
    endpoint: apiRoutes.PIPEDRIVE_UPDATE_AND_CONFIRM_ORDER_LIST,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.write'],
    },
  },
  apiPlatformList: {
    endpoint: apiRoutes.PLATFORM_LIST,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.write'],
    },
  },
  apiSalesAccountList: {
    endpoint: apiRoutes.SALES_ACCOUNT_LIST,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.write'],
    },
  },
  apiSellTypeList: {
    endpoint: apiRoutes.SELL_TYPE_LIST,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.write'],
    },
  },
  apiCategoryType: {
    endpoint: apiRoutes.CATEGORY_TYPE,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.write'],
    },
  },
  apiTrafficAccountList: {
    endpoint: apiRoutes.TRAFFIC_ACCOUNT_LIST,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.write'],
    },
  },
  apiReportCampaignPaginated: {
    endpoint: apiRoutes.REPORT_CAMPAIGN_PAGINATED,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.write'],
    },
  },
  apiReportCampaignNational: {
    endpoint: apiRoutes.REPORT_CAMPAIGN_NATIONAL,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.write'],
    },
  },
  apiReportMobility: {
    endpoint: apiRoutes.REPORT_MOBILITY,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.write'],
    },
  },
  apiReportAdministration: {
    endpoint: apiRoutes.REPORT_ADMINISTRATION,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.write'],
    },
  },
  apiReportAigen: {
    endpoint: apiRoutes.REPORT_AIGEN,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.write'],
    },
  },
  apiReportList: {
    endpoint: apiRoutes.REPORT_LIST,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.write'],
    },
  },
  apiReportDownload: {
    endpoint: apiRoutes.REPORT_DOWNLOAD,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.write'],
    },
  },
  apiReportDaily: {
    endpoint: apiRoutes.REPORT_DAILY,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.write'],
    },
  },
  apiReportVisit: {
    endpoint: apiRoutes.REPORT_VISIT,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.write'],
    },
  },
  apiCampaignListAll: {
    endpoint: apiRoutes.CAMPAIGN_LIST_ALL,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.write'],
    },
  },
  apiCampaignListStatus: {
    endpoint: apiRoutes.CAMPAIGN_LIST_STATUS,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.write'],
    },
  },
  apiCampaignListMonitorSales: {
    endpoint: apiRoutes.CAMPAIGN_LIST_MONITOR_SALE,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.write'],
    },
  },
  apiCampaignInsightAttachment: {
    endpoint: apiRoutes.CAMPAIGN_INSIGHT_ATTACHMENT,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.write'],
    },
  },
  apiCampaignInsightPlatform: {
    endpoint: apiRoutes.CAMPAIGN_INSIGHT_PLATFORM,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.write'],
    },
  },
  apiCampaignInsightDetails: {
    endpoint: apiRoutes.CAMPAIGN_INSIGHT_DETAILS,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.write'],
    },
  },
  apiCampaignInsightHicmobile: {
    endpoint: apiRoutes.CAMPAIGN_INSIGHT_HICMOBILE,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.write'],
    },
  },
  apiCampaignInsightClient: {
    endpoint: apiRoutes.CAMPAIGN_INSIGHT_CLIENT,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.write'],
    },
  },
  apiCampaignInsightScreens: {
    endpoint: apiRoutes.CAMPAIGN_INSIGHT_SCREENS,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.write'],
    },
  },
  apiCampaignInsightScreensSendMail: {
    endpoint: apiRoutes.CAMPAIGN_INSIGHT_SCREENS_SEND_MAIL,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.write'],
    },
  },
  apiCampaignInsightScreenAddDelete: {
    endpoint: apiRoutes.CAMPAIGN_INSIGHT_SCREENS_ADD_DELETE,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.write'],
    },
  },
  apiCampaignInsightDailyNote: {
    endpoint: apiRoutes.CAMPAIGN_INSIGHT_DAILY_NOTE,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.write'],
    },
  },
  apiCampaignInsightDailyNoteUpdate: {
    endpoint: apiRoutes.CAMPAIGN_INSIGHT_DAILY_NOTE_UPDATE,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.write'],
    },
  },
  apiCampaignInsightDeliveryNote: {
    endpoint: apiRoutes.CAMPAIGN_INSIGHT_DELIVERY_NOTE,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.write'],
    },
  },
  apiCampaignInsightDeliveryNoteUpdate: {
    endpoint: apiRoutes.CAMPAIGN_INSIGHT_DELIVERY_NOTE_UPDATE,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.write'],
    },
  },
  apiCampaignInsightTopPublishers: {
    endpoint: apiRoutes.CAMPAIGN_INSIGHT_TOP_PUBLISHERS,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.write'],
    },
  },
  apiCampaignSendReport: {
    endpoint: apiRoutes.CAMPAIGN_SENDREPORT,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.write'],
    },
  },
  apiKPICategoryType: {
    endpoint: apiRoutes.KPI_CATEGORY_TYPE,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.write'],
    },
  },
  apiAddKPIClient: {
    endpoint: apiRoutes.ADD_EDIT_KPI_CLIENT,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.write'],
    },
  },
  apiAddEditAdvertiser: {
    endpoint: apiRoutes.ADVERTISER,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.write'],
    },
  },
  apiAdminAccountList: {
    endpoint: apiRoutes.ADMIN_ACCOUNT_LIST,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.write'],
    },
  },
  apiPublishKPI: {
    endpoint: apiRoutes.CAMPAIGN_INSIGHT_PUBLISH,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.write'],
    },
  },
  apiPublishAllKPI: {
    endpoint: apiRoutes.CAMPAIGN_INSIGHT_PUBLISH_ALL,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.write'],
    },
  },
  apiActivateOrder: {
    endpoint: apiRoutes.CAMPAIGN_ACTIVATE,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.write'],
    },
  },
  apiFileList: {
    endpoint: apiRoutes.FILE_LIST,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.write'],
    },
  },
  apiFootfallFIle: {
    endpoint: apiRoutes.FOOTFALL_FILE,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.write'],
    },
  },
  apiFootfallPdvs: {
    endpoint: apiRoutes.FOOTFALL_PDV,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.write'],
    },
  },
  apiFootfallPowerpoint: {
    endpoint: apiRoutes.FOOTFALL_POWERPOINT,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.write'],
    },
  },
  apiFootfallKPI: {
    endpoint: apiRoutes.FOOTFALL_KPI,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/task-api/footfall.write'],
    },
  },
  apiPrecampignList: {
    endpoint: apiRoutes.PRECAMPAIGN_LIST,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.write'],
    },
  },
  apiPrecampignKPI: {
    endpoint: apiRoutes.PRECAMPAIGN_KPI,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.write'],
    },
  },
  apiPrecampignAdd: {
    endpoint: apiRoutes.PRECAMPAIGN_CREATE,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.write'],
    },
  },
  apiPrecampigPresentation: {
    endpoint: apiRoutes.PRECAMPAIGN_PRESENTATION,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.write'],
    },
  },
  apiPrecampignKPI_UPDATE: {
    endpoint: apiRoutes.PRECAMPAIGN_KPI_UPDATE,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.write'],
    },
  },
  apiPrecampignAudienceCategory: {
    endpoint: apiRoutes.PRECAMPAIGN_AUDIENCE_CATEGORY,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.write'],
    },
  },
  apiPrecampignAudienceCategoryLoad: {
    endpoint: apiRoutes.PRECAMPAIGN_AUDIENCE_CATEGORY_LOAD,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.write'],
    },
  },
  apiMerchantAllChannel: {
    endpoint: apiRoutes.MERCHANT_ALL_CHANNEL,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/merchant.write'],
    },
  },
  apiMerchantInfo: {
    endpoint: apiRoutes.MERCHANT_INFO,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/merchant.write'],
    },
  },
  apiMerchantEvaluate: {
    endpoint: apiRoutes.MERCHANT_EVALUATE,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/merchant.write'],
    },
  },
  apiMerchantCategories: {
    endpoint: apiRoutes.MERCHANT_CATEGORIES,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/merchant.write'],
    },
  },
  apiMerchantInsight: {
    endpoint: apiRoutes.MERCHANT_INSIGHTS,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/merchant.write'],
    },
  },
  apiLocationSearch: {
    endpoint: apiRoutes.LOCATIONS,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/merchant.write'],
    },
  },
  apiLocationSearchDFP: {
    endpoint: apiRoutes.LOCATIONS_DFP,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/merchant.write'],
    },
  },
  apiGeocodeAddress: {
    endpoint: apiRoutes.GEOCODEADDRESS,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/merchant.write'],
    },
  },
  apiGetInsightRadius: {
    endpoint: apiRoutes.GETINSIGHTS_RADIUS,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/merchant.write'],
    },
  },
  apiTargetFinderMinMax: {
    endpoint: apiRoutes.TARGETFINDER_MINMAX,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/merchant.write'],
    },
  },
  apiTargetFinderGet: {
    endpoint: apiRoutes.TARGETFINDER_GET,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/merchant.write'],
    },
  },
  apiTargetFinderUpdate: {
    endpoint: apiRoutes.TARGETFINDER_UPDATE,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/merchant.write'],
    },
  },
  apiTargetFinderSendBrief: {
    endpoint: apiRoutes.TARGETFINDER_SENDBRIEF,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/merchant.write'],
    },
  },
  apiTargetFinderMediaList: {
    endpoint: apiRoutes.TARGETFINDER_MEDIA,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/merchant.write'],
    },
  },
  apiTargetFinderSellTypeList: {
    endpoint: apiRoutes.TARGETFINDER_SELLTYPE,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/merchant.write'],
    },
  },
  apiTargetFinderMinMaxSellType: {
    endpoint: apiRoutes.TARGETFINDER_SELLTYPEMINMAX,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/merchant.write'],
    },
  },
  apiTargetFinderReporstList: {
    endpoint: apiRoutes.TARGETFINDER_REPORTS,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/merchant.write'],
    },
  },
  apiTargetFinderGetSingle: {
    endpoint: apiRoutes.TARGETFINDER_GETSINGLE,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/merchant.write'],
    },
  },
  apiTargetFinderBUssinessCategory: {
    endpoint: apiRoutes.TARGETFINDER_BUSSINESSCATEGORY,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/merchant.write'],
    },
  },
  apiTargetFinderBrief: {
    endpoint: apiRoutes.TARGETFINDER_BRIEF,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/merchant.write'],
    },
  },
  apiTargetFInderGetEdit: {
    endpoint: apiRoutes.TARGETFINDER_GETEDIT,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/merchant.write'],
    },
  },
  apiTargetFinderSuggestion: {
    endpoint: apiRoutes.TARGETFINDER_SUGGESTION,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/merchant.write'],
    },
  },
  apiKmlSubmit: {
    endpoint: apiRoutes.KML_SUBMIT,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/merchant.write'],
    },
  },
  apiMaestroGetAllDeals: {
    endpoint: apiRoutes.MAESTRO_GETALLDEALS,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/merchant.write'],
    },
  },
  apiMaestroCreateDeal: {
    endpoint: apiRoutes.MAESTRO_CREATEDEAL,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/merchant.write'],
    },
  },
  apiMaestroUpdateDeal: {
    endpoint: apiRoutes.MAESTRO_UPDATEDEAL,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/merchant.write'],
    },
  },
  apiGetDNAgency: {
    endpoint: apiRoutes.DN_GETAGENCY,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/merchant.write'],
    },
  },
  apiAddDNAgency: {
    endpoint: apiRoutes.DN_ADDAGENCY,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/merchant.write'],
    },
  },
  apiUploadInsights: {
    endpoint: apiRoutes.UPLOAD_INSIGHTS,
    scopes: {
      read: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.read'],
      write: ['https://dashicmobile.onmicrosoft.com/task-api/tasks.write'],
    },
  },
};

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = {
    scopes: [...protectedResources.apiUserDetails.scopes.read, ...protectedResources.apiUserDetails.scopes.write
    ],
};